import type { ReactNode, RefObject } from 'react'
import { Separator } from '@components/ui/separator'
import { TrayFiltersSection } from './tray-filters-section'
import { TrayResultsToolbar } from './tray-results-toolbar'
import { TrayScrollRoot } from './tray-scroll-root'
import { TraySortButton, type TraySortButtonProps } from './tray-sort-button'
import { TrayToggleRow } from './tray-toggle-row'

type TrayPanelLayoutProps<S extends string> = {
  /** The tray's scroll container — also the scroll root its `useTrayLazyWindow` observes. */
  scrollRootRef?: RefObject<HTMLDivElement | null>
  /** Album / Category / Type picker above the Filters section. */
  picker: ReactNode
  filtersOpen: boolean
  onFiltersOpenChange: (open: boolean) => void
  activeFilterCount: number
  onClearFilters: () => void
  /** Filter controls rendered inside the expanded Filters section. */
  filters: ReactNode
  /** Draws a rule between the Filters section and "Show filename" (Photos). */
  separatorAfterFilters?: boolean
  showFilename: boolean
  onShowFilenameChange: (checked: boolean) => void
  resultCount: number
  showResultCount: boolean
  sort: TraySortButtonProps<S>
  /** Pins the picker, Filters section, "Show filename" switch and results/Sort row above the
   *  scroll area, so only the gallery scrolls (Templates, US-11). Off by default: the other
   *  trays scroll the whole panel as one box (WMPCM-11930). */
  fixedHeader?: boolean
  /** Status messages and the results grid, below the header controls. */
  children?: ReactNode
}

/**
 * Shared right-panel tray layout (Photos, Templates, Backgrounds, ClikArt): picker, Filters
 * section, "Show filename" switch and the results count + Sort row, followed by the tray's own
 * body. By default the whole panel is one scroll box; with `fixedHeader` the controls stay put
 * and only the body scrolls.
 */
export function TrayPanelLayout<S extends string>({
  scrollRootRef,
  picker,
  filtersOpen,
  onFiltersOpenChange,
  activeFilterCount,
  onClearFilters,
  filters,
  separatorAfterFilters = false,
  showFilename,
  onShowFilenameChange,
  resultCount,
  showResultCount,
  sort,
  fixedHeader = false,
  children,
}: TrayPanelLayoutProps<S>) {
  const header = (
    <div className="flex w-full min-w-0 shrink-0 flex-col gap-3 pb-2">
      {picker}

      <TrayFiltersSection
        open={filtersOpen}
        onOpenChange={onFiltersOpenChange}
        activeCount={activeFilterCount}
        onClear={onClearFilters}
      >
        {filters}
      </TrayFiltersSection>

      {separatorAfterFilters ? <Separator className="w-full" /> : null}

      <TrayToggleRow
        label="Show filename"
        checked={showFilename}
        onCheckedChange={onShowFilenameChange}
      />

      <TrayResultsToolbar count={resultCount} showCount={showResultCount}>
        <TraySortButton {...sort} />
      </TrayResultsToolbar>
    </div>
  )

  if (fixedHeader) {
    return (
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {/* Capped so a tall expanded Filters section can't squeeze the gallery to nothing — past
            the cap the controls scroll within themselves. The same gutter reservation as the
            gallery below keeps both columns the same width. */}
        <div
          data-testid="tray-fixed-header"
          className="max-h-[60%] shrink-0 overflow-x-hidden overflow-y-auto [scrollbar-gutter:stable]"
        >
          {header}
        </div>
        <div
          ref={scrollRootRef}
          data-testid="tray-scroll-body"
          className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-auto [scrollbar-gutter:stable]"
        >
          {children}
        </div>
      </div>
    )
  }

  return (
    <TrayScrollRoot scrollRootRef={scrollRootRef}>
      {header}

      {children}
    </TrayScrollRoot>
  )
}
